import React, { useState } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';

interface Question {
  id?: number;
  title: string;
  type: 'radio' | 'checkbox' | 'text';
  required: boolean;
  order: number;
  options?: Option[];
}

interface Option {
  value: string;
  label: string;
  order: number;
}

interface QuestionEditorProps {
  question: Question;
  onChange: (question: Question) => void;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({ question, onChange }) => {
  const [localQuestion, setLocalQuestion] = useState<Question>(question);

  const updateQuestion = (updates: Partial<Question>) => {
    const updated = { ...localQuestion, ...updates };
    setLocalQuestion(updated);
    onChange(updated);
  };

  const handleAddOption = () => {
    const newOptions = [
      ...(localQuestion.options || []),
      {
        value: `option_${Date.now()}`,
        label: '',
        order: localQuestion.options?.length || 0
      }
    ];
    updateQuestion({ options: newOptions });
  };

  const handleUpdateOption = (index: number, updates: Partial<Option>) => {
    const newOptions = [...(localQuestion.options || [])];
    newOptions[index] = { ...newOptions[index], ...updates };
    updateQuestion({ options: newOptions });
  };

  const handleDeleteOption = (index: number) => {
    const newOptions = localQuestion.options?.filter((_, i) => i !== index) || [];
    // Пересчитываем порядок
    newOptions.forEach((opt, i) => {
      opt.order = i;
    });
    updateQuestion({ options: newOptions });
  };

  const handleMoveOption = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === (localQuestion.options?.length || 0) - 1) return;

    const newOptions = [...(localQuestion.options || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newOptions[index], newOptions[newIndex]] = [newOptions[newIndex], newOptions[index]];
    newOptions[index].order = index;
    newOptions[newIndex].order = newIndex;
    updateQuestion({ options: newOptions });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Текст вопроса *
        </label>
        <input
          type="text"
          value={localQuestion.title}
          onChange={(e) => updateQuestion({ title: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Введите текст вопроса"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Тип вопроса *
          </label>
          <select
            value={localQuestion.type}
            onChange={(e) => updateQuestion({ type: e.target.value as 'radio' | 'checkbox' | 'text' })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="radio">Один вариант (radio)</option>
            <option value="checkbox">Несколько вариантов (checkbox)</option>
            <option value="text">Текстовый ответ</option>
          </select>
        </div>

        <div className="flex items-center">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={localQuestion.required}
              onChange={(e) => updateQuestion({ required: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Обязательный вопрос</span>
          </label>
        </div>
      </div>

      {(localQuestion.type === 'radio' || localQuestion.type === 'checkbox') && (
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Варианты ответов *
            </label>
            <button
              type="button"
              onClick={handleAddOption}
              className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Добавить вариант</span>
            </button>
          </div>

          {(!localQuestion.options || localQuestion.options.length === 0) ? (
            <div className="text-center py-4 text-gray-500 text-sm border border-gray-200 rounded-lg">
              Нет вариантов ответов. Добавьте хотя бы один вариант.
            </div>
          ) : (
            <div className="space-y-2">
              {localQuestion.options.map((option, index) => (
                <div key={index} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg">
                  <GripVertical className="h-5 w-5 text-gray-400" />
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={option.value}
                      onChange={(e) => handleUpdateOption(index, { value: e.target.value })}
                      placeholder="Значение (value)"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => handleUpdateOption(index, { label: e.target.value })}
                      placeholder="Текст варианта (label)"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => handleMoveOption(index, 'up')}
                      disabled={index === 0}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      title="Вверх"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveOption(index, 'down')}
                      disabled={index === (localQuestion.options?.length || 0) - 1}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      title="Вниз"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteOption(index)}
                      className="text-red-600 hover:text-red-800"
                      title="Удалить"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {localQuestion.type === 'text' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            Для текстового вопроса варианты ответов не требуются. Пользователь введет текст самостоятельно.
          </p>
        </div>
      )}
    </div>
  );
};

export default QuestionEditor;

